/**
 * v-overflow-tooltip directive
 *
 * Shows a tooltip only when the element's text is truncated (overflow).
 * Uses tippy.js with onShow guard to check scrollWidth vs clientWidth.
 *
 * Usage:
 *   v-overflow-tooltip="'Full text here'"
 *   v-overflow-tooltip="{ content: 'Full text', placement: 'bottom' }"
 */
import tippy from 'tippy.js'

import { sanitizeHtml } from '@/utils/sanitize'

const DISTANCE = 10

const defaults = {
  allowHTML: true,
  delay: [200, 100],
  offset: [0, DISTANCE],
  arrow: true,
  hideOnClick: true,
  interactive: true,
  appendTo: () => document.body,
  // Bound to viewport so overflow:hidden ancestors don't clip the tooltip
  popperOptions: {
    modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
  },
}

function sanitizeContent(content) {
  return typeof content === 'string' ? sanitizeHtml(content) : content
}

function isOverflowing(el) {
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
}

function resolveContent(value) {
  if (value == null || value === false) return null
  if (typeof value === 'string') return { content: value }
  if (typeof value === 'object') return value
  return null
}

function createInstance(el, value) {
  const opts = resolveContent(value)
  if (!opts?.content) return

  el._overflow_tippy = tippy(el, {
    ...defaults,
    content: sanitizeContent(opts.content),
    placement: opts.placement || 'top',
    onShow: () => isOverflowing(el),
  })
}

export const overflowTooltip = {
  beforeMount(el, { value }) {
    createInstance(el, value)
  },

  updated(el, { value, oldValue }) {
    if (value === oldValue) return
    const opts = resolveContent(value)

    if (!opts?.content) {
      el._overflow_tippy?.destroy()
      el._overflow_tippy = null
      return
    }

    if (el._overflow_tippy) {
      el._overflow_tippy.setProps({ content: sanitizeContent(opts.content) })
    } else {
      createInstance(el, value)
    }
  },

  beforeUnmount(el) {
    el._overflow_tippy?.destroy()
    el._overflow_tippy = null
  },
}
